import { type Dispatch, type SetStateAction, useEffect, useRef, useState } from "react";
import type { IAnchorElements } from ".";

const getIndexFromId = (id: string, elementsToObserve: (Element | null)[]) => {
  return elementsToObserve.findIndex((heading) => heading?.id === id);
};

export const useIntersectionObserver = (
  setActiveId: Dispatch<SetStateAction<string | undefined>>,
  isBottom: boolean,
  anchorElements: IAnchorElements[],
  parentId?: string
) => {
  const [elementsToObserve, setElementsToObserve] = useState<(Element | null)[]>([]);
  const headingElementsRef = useRef<Record<string, IntersectionObserverEntry>>({});

  useEffect(() => setElementsToObserve(anchorElements.map(({ anchorId }) => document.getElementById(anchorId))), []);

  useEffect(() => {
    const callback = (headings: IntersectionObserverEntry[]) => {
      headingElementsRef.current = headings.reduce((map, headingElement) => {
        map[headingElement.target.id] = headingElement;
        return map;
      }, headingElementsRef.current);

      // Get all headings that are currently visible on the page
      const visibleHeadings: IntersectionObserverEntry[] = [];
      Object.keys(headingElementsRef.current).forEach((key) => {
        const headingElement = headingElementsRef.current[key];
        if (headingElement.isIntersecting) {
          visibleHeadings.push(headingElement);
        }
      });

      // If there is only one visible heading, this is our "active" heading
      if (visibleHeadings.length === 1) {
        setActiveId(visibleHeadings[0].target.id);
        // If there is more than one visible heading,
        // choose the one that is closest to the top of the page
      } else if (visibleHeadings.length > 1) {
        const sortedVisibleHeadings = visibleHeadings.sort(
          (a, b) => getIndexFromId(a.target.id, elementsToObserve) - getIndexFromId(b.target.id, elementsToObserve)
        );

        if (isBottom) {
          setActiveId(sortedVisibleHeadings[sortedVisibleHeadings.length - 1].target.id);
        } else {
          setActiveId(sortedVisibleHeadings[0].target.id);
        }
      }
    };

    const observer = new IntersectionObserver(callback, {
      root: document.getElementById(parentId || "root"),
      rootMargin: "0px",
    });

    for (const element of elementsToObserve) {
      if (element) {
        observer.observe(element);
      }
    }

    return () => observer.disconnect();
  }, [setActiveId, isBottom, parentId, elementsToObserve]);
};

export const scrollToElement = (id: string) => {
  const element = document.getElementById(id);
  if (element) {
    element.scrollIntoView({
      behavior: "smooth",
      block: "start",
      inline: "nearest",
    });
  }
};
